import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Loader2 } from 'lucide-react';
import EToast from '@/components/everridge/EToast';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import { usePlatformSettingsApi } from './hooks/usePlatformSettingsApi';
import { usePlatformSettingsUpdateMutation } from './hooks/usePlatformSettingsMutation';

const PlatformGeneralSettingsPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userRole = useUserRole();

    const detailQ = usePlatformSettingsApi();
    const updateMut = usePlatformSettingsUpdateMutation(() => {
        EToast({ variant: 'success', message: t('superAdminSettings.general.toastSaved') });
    });

    const [supportEmailKnowledgeBaseText, setSupportEmailKnowledgeBaseText] = useState('');

    useEffect(() => {
        if (!detailQ.data) return;
        setSupportEmailKnowledgeBaseText(detailQ.data.supportEmailKnowledgeBaseText ?? '');
    }, [detailQ.data, detailQ.dataUpdatedAt]);

    if (userRole !== Roles.superAdmin) return null;

    const handleSave = () => {
        updateMut.mutate(
            {
                supportEmailKnowledgeBaseText: supportEmailKnowledgeBaseText.trim() || null,
            },
            {
                onError: () =>
                    EToast({ variant: 'error', message: t('superAdminSettings.general.toastFail') }),
            },
        );
    };

    const loading = detailQ.isLoading;

    return (
        <section className="flex h-full min-h-0 flex-col overflow-auto p-6">
            <div className="mb-6 flex flex-wrap items-center gap-3">
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => navigate('/superAdmin/dashboard')}
                >
                    <ChevronLeft className="size-4" />
                </Button>
                <h1 className="text-xl font-medium text-black">
                    {t('superAdminSettings.general.pageTitle')}
                </h1>
            </div>

            <p className="mx-auto mb-6 w-full max-w-lg text-sm text-gray">
                {t('superAdminSettings.general.subtitle')}
            </p>

            {loading ? (
                <div className="flex min-h-40 items-center justify-center">
                    <Loader2 className="size-8 animate-spin text-primary-color-1000" />
                </div>
            ) : detailQ.error ? (
                <p className="text-sm text-destructive">{t('superAdminSettings.general.toastFail')}</p>
            ) : (
                <div className="mx-auto w-full max-w-lg space-y-8">
                    <div className="space-y-4">
                        <div>
                            <h2 className="text-base font-medium text-black">
                                {t('superAdminSettings.general.supportEmailKbSectionTitle')}
                            </h2>
                            <p className="mt-1 text-xs text-gray">
                                {t('superAdminSettings.general.supportEmailKbSectionDesc')}
                            </p>
                        </div>
                        <label className="flex flex-col gap-1">
                            <textarea
                                value={supportEmailKnowledgeBaseText}
                                onChange={(e) => setSupportEmailKnowledgeBaseText(e.target.value)}
                                rows={8}
                                maxLength={12000}
                                className="rounded-lg border border-lightgray bg-base px-3 py-2 text-sm text-black"
                            />
                            <p className="text-xs text-gray">
                                {t('superAdminSettings.general.supportEmailKbHint', {
                                    count: supportEmailKnowledgeBaseText.length,
                                })}
                            </p>
                        </label>
                    </div>

                    <div className="space-y-4 border-t border-lightgray pt-6">
                        <div>
                            <h2 className="text-base font-medium text-black">
                                {t('superAdminSettings.general.appAiSectionTitle')}
                            </h2>
                            <p className="mt-1 text-xs text-gray">
                                {t('superAdminSettings.general.appAiSectionDesc')}
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => navigate('/superAdmin/settings/ai')}
                        >
                            {t('superAdminSettings.general.openAppAiSettings')}
                        </Button>
                    </div>

                    <Button type="button" disabled={updateMut.isPending} onClick={handleSave}>
                        {t('superAdminSettings.general.save')}
                    </Button>
                </div>
            )}
        </section>
    );
};

export default PlatformGeneralSettingsPage;
